import { html, View } from 'rune-ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { FundingModalFactory } from '@services/MarppleShop/renderApp/components/cells/funding/FundingModal.ts';
import klass from './Funding.module.scss';
import { font, staticFont } from '@services/MarppleShop/shared/typography';
import format from 'date-fns/format/index.js';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import { FundingTexts } from '@services/MarppleShop/renderApp/components/cells/funding/FundingTexts.ts';
import { SeeMoreLinkPlugin } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/plugins';
import { SeeMoreLinkView } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/views/SeeMoreLinkView/SeeMoreLinkView.ts';
import { FundingModalController } from '@services/MarppleShop/renderApp/components/cells/funding/FundingModalController.ts';
import { pipe, map, toArray, zipWithIndex, take } from '@fxts/core';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';
import { MShopAppStoreCommunityConstantS } from '@modules/MShop/App/Store/Community/S/Constant/module/MShopAppStoreCommunityConstantS';
import { htmlIfElse } from '@services/MarppleShop/shared/util';
import { SupporterRankingsResponse } from '@services/MarppleShop/features/Funding';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';

export interface FundingSectionProps {
  product_id: number;
  supporters: SupporterRankingsResponse;
  is_mobile?: boolean;
}

export class FundingTopRankSupporterItem extends View<
  SupporterRankingsResponse['data'][number] & { rank: number }
> {
  protected override template() {
    return html`
      <div class="${klass.ranker_supporter}" data-is-compact="${this.data.rank !== 1}">
        <div class="${klass.ranker_supporter_badge}">
          ${FundingModalController.makeRankHtml(this.data.rank)}
        </div>

        <div class="${klass.ranker_supporter_image}">
          <img
            src="${getResizedUrl({
              url: this.data.profile_image || MShopAppStoreCommunityConstantS.EMPTY_USER_IMG,
              quality: 85,
              width: 88 * 2,
            })}"
            onerror="this.onerror=null; this.src='${MShopAppStoreCommunityConstantS.EMPTY_USER_IMG}'"
            alt=""
          />
        </div>

        <div class="${klass.ranker_supporter_profile}">
          <p class="${font('14_medium')}">${this.data.nick_name}</p>
          <span class="${font('12_medium', '10_medium')}"
            >${format(new Date(this.data.created_at), 'yyyy.MM.dd HH:mm')}</span
          >
        </div>
      </div>
    `;
  }
}

export class FundingSupporterItem extends View<
  SupporterRankingsResponse['data'][number] & { rank?: number }
> {
  protected override template() {
    return html`
      <li class="${klass.supporter}">
        ${this.data.rank
          ? html`<div class="${klass.supporter_rank} ${staticFont('bebas_16_bold')}">
              ${FundingModalController.makeRankHtml(this.data.rank)}
            </div>`
          : ''}
        <div class="${klass.supporter_profile}">
          <img
            class="${klass.supporter_profile_image}"
            src="${getResizedUrl({
              url: this.data.profile_image || MShopAppStoreCommunityConstantS.EMPTY_USER_IMG,
              quality: 85,
              width: 44 * 2,
            })}"
            onerror="this.onerror=null; this.src='${MShopAppStoreCommunityConstantS.EMPTY_USER_IMG}'"
            alt=""
          />
          <div class="${klass.supporter_profile_detail}">
            <p class="${font('14_medium')}">
              ${htmlIfElse(
                !!this.data.rank,
                this.data.nick_name,
                FundingTexts.texts().modal.item.label(this.data.nick_name),
              )}
            </p>
            <span class="${font('12_medium')}"
              >${format(new Date(this.data.created_at), 'yyyy.MM.dd HH:mm')}</span
            >
          </div>
        </div>
      </li>
    `;
  }
}

export class FundingSection extends View<FundingSectionProps> {
  sectionTitleView: SectionTitle;
  fundingModalView: Modal;
  texts = FundingTexts.texts();

  constructor(props: FundingSectionProps) {
    super(props);

    this.sectionTitleView = new SectionTitle({
      title_props: {
        title: this.texts.section.ranking_title,
        size: this.data.is_mobile ? 'xSmall' : 'medium',
      },
      plugins: [
        new SeeMoreLinkPlugin({
          size: this.data.is_mobile ? 'xSmall' : 'medium',
        }),
      ],
    });
    this.fundingModalView = FundingModalFactory.create(props);
  }

  protected override template() {
    return html`
      <div class="${klass.section}">
        ${this.sectionTitleView}

        <ul class="${klass.supporters}">
          ${pipe(
            this.data.supporters.data,
            take(this.data.is_mobile ? 3 : 6),
            zipWithIndex,
            map(([rank, supporter]) => new FundingSupporterItem({ ...supporter, rank: rank + 1 })),
            toArray,
          )}
        </ul>

        ${this.fundingModalView}
      </div>
    `;
  }

  protected override onRender() {
    this.delegate('click', SeeMoreLinkView, () => {
      this.fundingModalView.open();
    });

    this.delegate('click', ButtonAction, () => {
      this.fundingModalView.close();
    });
  }
}
